import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

export enum UnreadBannerPositionsEnum {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

interface UnreadBannerBaseProps {
  currentPosition: UnreadBannerPositionsEnum;
  label: ReactNode;
  onNavigateToUnreadClick: Handler;
  onDismissBannerClick: Handler;
}

/*
 * Style.
 */

type StyleProps = {
  $currentPosition?: UnreadBannerPositionsEnum;
};

const StyledUnreadBannerDiv = styled.div<StyleProps>`
  position: absolute;
  width: 100%;
  height: 34px;
  opacity: 0.95;
  align-self: ${(p) => (p.$currentPosition === UnreadBannerPositionsEnum.TOP ? 'flex-start' : 'flex-end')};

  background-color: ${(p) => p.theme.greys.shade10};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.palette.blue.shade40};
  ${center()};
  ${(p) => alignBanner(p.$currentPosition)}

  &:hover {
    background-color: ${(p) => p.theme.greys.shade20};
    color: ${(p) => p.theme.palette.blue.shade50};
  }

  ${(p) => maybeAddFloatingStyles(p.$currentPosition)};
`;

function maybeAddFloatingStyles(position?: UnreadBannerPositionsEnum) {
  return css`
    width: auto;
    height: 30px;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${(p) => p.theme.greys.shade20};
    color: ${(p) => p.theme.palette.blue.shade40};
    border-radius: 100px;

    &:hover {
      background-color: ${(p) => p.theme.greys.shade30};
      color: ${(p) => p.theme.palette.blue.shade40};
    }

    ${alignFloatingBanner(position)};
  `;
}

function alignFloatingBanner(position?: UnreadBannerPositionsEnum) {
  if (position === UnreadBannerPositionsEnum.TOP) {
    return css`
      top: 8px;
    `;
  }

  return css`
    bottom: 8px;
  `;
}

function alignBanner(position?: UnreadBannerPositionsEnum) {
  if (position === UnreadBannerPositionsEnum.TOP) {
    return css`
      top: 0;
    `;
  }

  return css`
    bottom: 0;
  `;
}

const StyledBannerContentDiv = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-column-gap: 7px;
`;

const StyledBannerLabelDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledIconDirectionSpan = styled.div<StyleProps>`
  margin-right: 6px;
  margin-left: 11px;
  ${(p) => (p.$currentPosition === UnreadBannerPositionsEnum.TOP ? `transform: rotate(180deg);` : '')}
`;

const StyledDismissButton = styled(ChromelessButton)`
  height: 26px;
  width: 26px;
  border-radius: 13px;
  color: ${(p) => p.theme.greys.shade80};
  ${center()};

  margin-right: 2px;
  &:hover {
    color: ${(p) => p.theme.greys.shade80};
    background-color: ${(p) => p.theme.alphas.gray20};
  }
`;

/*
 * Component.
 */

export const UnreadBannerBase: FC<UnreadBannerBaseProps> = (props) => {
  const {currentPosition, label, onNavigateToUnreadClick, onDismissBannerClick} = props;

  return (
    <StyledUnreadBannerDiv $currentPosition={currentPosition}>
      <StyledBannerContentDiv>
        <StyledBannerLabelDiv onClick={onNavigateToUnreadClick}>
          <StyledIconDirectionSpan $currentPosition={currentPosition}>
            <Icon name="arrowThickSmall" />
          </StyledIconDirectionSpan>
          {label}
        </StyledBannerLabelDiv>
        <StyledDismissButton onClick={onDismissBannerClick}>
          <Icon name="closeSmall" />
        </StyledDismissButton>
      </StyledBannerContentDiv>
    </StyledUnreadBannerDiv>
  );
};
